/**
 * Adventure Map Page
 *
 * The hex overworld page for co-op adventures. Shows a simple HTML/CSS hex grid
 * (temporary, will be replaced by PixiJS renderer later), encounter preview modal,
 * team status panel, and handles all socket events for map navigation.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdventureStore, type AdventureData, type WorldData } from '../stores/useAdventureStore';
import { adventureSocket, type AdventureEncounterPreviewPayload } from '../services/adventure-socket';
import { useAuth } from '../contexts/AuthContext';
import { EncounterPreviewModal } from '../components/CoOp/EncounterPreviewModal';
import { TeamStatusPanel } from '../components/CoOp/TeamStatusPanel';
import { PageAtmosphere } from '../components/atmosphere';
import './AdventureMapPage.css';

// =====================================================================
// HEX MAP GRID (inline temporary component)
// =====================================================================

interface HexCellData {
  q: number;
  r: number;
  type: string;
  terrain?: string;
  encounterId?: string;
  gatedBy?: string;
  healPercent?: number;
  revive?: boolean;
  flavorText?: string;
}

interface HexMapGridProps {
  hexes: HexCellData[];
  mapWidth: number;
  mapHeight: number;
  revealedHexes: Set<string>;
  currentPosition: { q: number; r: number } | null;
  completedEncounters: Record<string, { stars: number }>;
  usedRestPoints: string[];
  onHexClick: (hex: { q: number; r: number }) => void;
  onCurrentEncounterClick?: (hex: HexCellData) => void;
}

function hexKey(q: number, r: number): string {
  return `${q},${r}`;
}

/** Check if two hexes are adjacent (cube distance = 1) */
function isAdjacent(a: { q: number; r: number }, b: { q: number; r: number }): boolean {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  const ds = (-a.q - a.r) - (-b.q - b.r);
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(ds)) === 2;
}

const HEX_TYPE_ICONS: Record<string, string> = {
  start: '\uD83C\uDFE0',
  path: '\u00B7',
  encounter: '\u2694\uFE0F',
  rest: '\u2764\uFE0F',
  boss: '\uD83D\uDC80',
  gate: '\uD83D\uDD12',
  treasure: '\uD83C\uDF81',
  blocked: '\u26D4',
};

const HexMapGrid: React.FC<HexMapGridProps> = ({
  hexes,
  mapWidth,
  mapHeight,
  revealedHexes,
  currentPosition,
  completedEncounters,
  usedRestPoints,
  onHexClick,
  onCurrentEncounterClick,
}) => {
  // Build rows from hex data
  const rows: HexCellData[][] = [];
  for (let r = 0; r < mapHeight; r++) {
    rows[r] = [];
  }

  // Index hexes by position
  const hexMap = new Map<string, HexCellData>();
  for (const hex of hexes) {
    hexMap.set(hexKey(hex.q, hex.r), hex);
    if (hex.r >= 0 && hex.r < mapHeight) {
      rows[hex.r].push(hex);
    }
  }

  // Sort each row by q
  for (const row of rows) {
    row.sort((a, b) => a.q - b.q);
  }

  return (
    <div className="hex-grid-container">
      {rows.map((row, rIdx) => (
        <div key={rIdx} className={`hex-row ${rIdx % 2 === 1 ? 'hex-row-offset' : ''}`}>
          {row.map((hex) => {
            const key = hexKey(hex.q, hex.r);
            const isRevealed = revealedHexes.has(key);
            const isCurrent = currentPosition?.q === hex.q && currentPosition?.r === hex.r;
            const isAdjacentToCurrent = currentPosition ? isAdjacent(currentPosition, hex) : false;
            const isCompleted = hex.encounterId ? !!completedEncounters[hex.encounterId] : false;
            const isRestUsed = usedRestPoints.includes(key);
            const isCurrentEncounter = isCurrent
              && (hex.type === 'encounter' || hex.type === 'boss')
              && hex.encounterId
              && !isCompleted;
            const canClick = isRevealed && ((isAdjacentToCurrent && !isCurrent) || !!isCurrentEncounter);

            let cellClass = 'hex-cell';
            if (!isRevealed) {
              cellClass += ' hex-fog';
            } else if (isCurrentEncounter) {
              cellClass += ' hex-current hex-encounter hex-clickable';
            } else if (isCurrent) {
              cellClass += ' hex-current';
            } else if (hex.type === 'encounter' && !isCompleted) {
              cellClass += ' hex-encounter';
            } else if (hex.type === 'rest' && !isRestUsed) {
              cellClass += ' hex-rest';
            } else if (hex.type === 'boss') {
              cellClass += ' hex-boss';
            } else if (hex.type === 'blocked' || hex.type === 'gate') {
              cellClass += ' hex-blocked';
            } else {
              cellClass += ' hex-revealed';
            }

            if (canClick) {
              cellClass += ' hex-clickable';
            }

            let icon = '';
            if (!isRevealed) {
              icon = '?';
            } else if (isCurrentEncounter) {
              icon = '\u2694\uFE0F'; // swords - click to fight
            } else if (isCurrent) {
              icon = '\uD83D\uDC63'; // footprints
            } else if (isCompleted) {
              const stars = completedEncounters[hex.encounterId!]?.stars || 0;
              icon = '\u2605'.repeat(stars) || '\u2713';
            } else if (isRestUsed) {
              icon = '\u2714';
            } else {
              icon = HEX_TYPE_ICONS[hex.type] || '\u00B7';
            }

            return (
              <div
                key={key}
                className={cellClass}
                onClick={() => {
                  if (!canClick) return;
                  if (isCurrentEncounter && onCurrentEncounterClick) {
                    onCurrentEncounterClick(hex);
                  } else {
                    onHexClick({ q: hex.q, r: hex.r });
                  }
                }}
                title={isRevealed
                  ? `${hex.type}${hex.flavorText ? `: ${hex.flavorText}` : ''} (${hex.q},${hex.r})`
                  : 'Unexplored'}
              >
                <span className="hex-icon">{icon}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// =====================================================================
// ADVENTURE MAP PAGE
// =====================================================================

export const AdventureMapPage: React.FC = () => {
  const { adventureId } = useParams<{ adventureId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const navigatingAwayRef = useRef(false);

  const {
    adventure,
    world,
    partnerConnected,
    revealedHexes,
    currentPosition,
    setFullState,
    updatePosition,
    setPartnerConnected,
    markRestPointUsed,
    updateCreatureStates,
    recordEncounterResult,
    setBattlePhase,
  } = useAdventureStore();

  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [showEncounterModal, setShowEncounterModal] = useState(false);
  const [encounterPreview, setEncounterPreview] = useState<AdventureEncounterPreviewPayload | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  // Show notification temporarily
  const showNotification = useCallback((msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // Connect socket on mount
  useEffect(() => {
    if (authLoading || !user || !adventureId) return;

    let mounted = true;

    const connectToAdventure = async () => {
      try {
        setConnectionStatus('connecting');

        const state = await adventureSocket.connect(adventureId);

        if (!mounted) return;

        if (state) {
          setFullState(
            state.adventure as AdventureData,
            state.world as WorldData,
            state.partnerConnected
          );
          setConnectionStatus('connected');
        }
      } catch (err) {
        if (!mounted) return;
        setConnectionStatus('error');
        setConnectionError(err instanceof Error ? err.message : 'Failed to connect');
      }
    };

    // Delay for auth token availability
    const timer = setTimeout(connectToAdventure, 150);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [authLoading, user, adventureId, setFullState]);

  // Register socket event listeners
  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    // Reset navigation flag when (re)entering the map page — ensures cleanup
    // runs properly if user navigated away to a battle and came back.
    navigatingAwayRef.current = false;

    // Clear any stale listeners from a previous mount (e.g. returning from battle
    // where unmount cleanup was intentionally skipped to keep the socket alive).
    adventureSocket.clearAdventureListeners();

    adventureSocket.onMoveConfirmed((data) => {
      updatePosition(data.newPosition, data.newReveals);
      setMoveError(null);
      if (data.flavorText) {
        showNotification(data.flavorText);
      }
    });

    adventureSocket.onMoveRejected((data) => {
      const reasonMessages: Record<string, string> = {
        encounter_not_cleared: 'Defeat the encounter before moving on!',
        not_adjacent: 'Too far away',
        not_revealed: 'Path not yet revealed',
        blocked: 'Path is blocked',
        boss_gate_locked: 'Defeat the required encounter to unlock this gate',
        not_active: 'Adventure is not active',
      };
      setMoveError(reasonMessages[data.reason] || data.reason);
      setTimeout(() => setMoveError(null), 3000);
    });

    adventureSocket.onEncounterPreview((data) => {
      setEncounterPreview(data);
      setShowEncounterModal(true);
    });

    adventureSocket.onBattleStarting((data) => {
      setShowEncounterModal(false);
      setBattlePhase('deployment', data.encounterId, data.matchId);
      navigatingAwayRef.current = true;

      // Get alive creature IDs from adventure state for the deployment grid
      const creatureStates = adventure?.creatureStates || {};
      const aliveCreatureIds = Object.entries(creatureStates)
        .filter(([, state]) => state.status !== 'dead')
        .map(([id]) => id);

      // Route to the shared deployment page with adventure mode
      const params = new URLSearchParams({
        mode: 'adventure',
        adventureId: adventureId!,
        encounterId: data.encounterId,
        matchId: data.matchId,
        creatures: aliveCreatureIds.join(','),
      });
      navigate(`/deployment?${params.toString()}`);
    });

    adventureSocket.onBattleCancelled(() => {
      setShowEncounterModal(false);
      setBattlePhase('none');
      showNotification('Battle cancelled');
    });

    adventureSocket.onBattleResult((data) => {
      recordEncounterResult(data.encounterId, {
        stars: data.stars,
        completedAt: new Date().toISOString(),
        attempts: data.encounterProgress.attempts,
      });
      if (data.updatedCreatureStates) {
        updateCreatureStates(data.updatedCreatureStates as Record<string, { currentHp: number; maxHp: number; status: string; ownerId: string }>);
      }
    });

    adventureSocket.onRestApplied((data) => {
      markRestPointUsed(data.restPointHex);
      const healedNames = data.healedCreatures
        .map(c => `${c.creatureId.slice(0, 10)}${c.revived ? ' (revived!)' : ''}`)
        .join(', ');
      showNotification(`Rest point healed: ${healedNames}`);
    });

    adventureSocket.onPartnerConnected(() => {
      setPartnerConnected(true);
      showNotification('Partner connected');
    });

    adventureSocket.onPartnerDisconnected(() => {
      setPartnerConnected(false);
      showNotification('Partner disconnected');
    });

    adventureSocket.onPartnerReconnected((data) => {
      setPartnerConnected(true);
      showNotification(`${data.displayName} reconnected`);
    });

    adventureSocket.onSessionPaused(() => {
      navigate('/adventure');
    });

    adventureSocket.onError((data) => {
      showNotification(`Error: ${data.message}`);
    });

    return () => {
      adventureSocket.clearAdventureListeners();
    };
  }, [
    connectionStatus,
    adventureId,
    navigate,
    updatePosition,
    setPartnerConnected,
    markRestPointUsed,
    updateCreatureStates,
    recordEncounterResult,
    setBattlePhase,
    showNotification,
  ]);

  // Cleanup on unmount -- don't disconnect if navigating to battle (socket reused)
  useEffect(() => {
    return () => {
      if (!navigatingAwayRef.current) {
        adventureSocket.clearAdventureListeners();
      }
    };
  }, []);

  // Handlers
  const handleHexClick = useCallback((hex: { q: number; r: number }) => {
    adventureSocket.move(hex);
  }, []);

  const handleCurrentEncounterClick = useCallback((hex: HexCellData) => {
    if (!hex.encounterId) return;
    // Re-trigger the encounter flow when clicking the current encounter hex
    adventureSocket.enterBattle(hex.encounterId);
  }, []);

  const handleEnterBattle = useCallback(() => {
    if (!encounterPreview) return;
    // enterBattle triggers the encounter; confirmBattle is called separately
    // by the deployment page when the player clicks Ready after place creatures
    adventureSocket.enterBattle(encounterPreview.encounterId);
    adventureSocket.confirmBattle(encounterPreview.encounterId);
  }, [encounterPreview]);

  const handleCloseEncounterModal = useCallback(() => {
    setShowEncounterModal(false);
    setEncounterPreview(null);
  }, []);

  const handleSaveAndExit = useCallback(() => {
    adventureSocket.saveAndExit();
    navigate('/adventure');
  }, [navigate]);

  // Loading / auth states
  if (authLoading) {
    return (
      <PageAtmosphere page="adventure-map">
        <div className="adventure-map">
          <div className="map-loading">
            <div className="loading-spinner" />
            <p>Authenticating...</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  if (connectionStatus === 'error') {
    return (
      <PageAtmosphere page="adventure-map">
        <div className="adventure-map">
          <div className="map-error">
            <h2>Connection Error</h2>
            <p>{connectionError || 'Failed to connect'}</p>
            <button onClick={() => navigate('/adventure')}>Back to Hub</button>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  if (connectionStatus === 'connecting' || !adventure || !world) {
    return (
      <PageAtmosphere page="adventure-map">
        <div className="adventure-map">
          <div className="map-loading">
            <div className="loading-spinner" />
            <p>Loading adventure map...</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  const themeEmoji: Record<string, string> = {
    fire: '\uD83C\uDF0B', ice: '\u2744\uFE0F', forest: '\uD83C\uDF32',
    dark: '\uD83C\uDF11', desert: '\uD83C\uDFDC\uFE0F', ocean: '\uD83C\uDF0A',
    mountain: '\u26F0\uFE0F',
  };

  return (
    <PageAtmosphere page="adventure-map">
    <div className="adventure-map">
      {/* Header */}
      <div className="map-header">
        <div className="map-header-left">
          <h1>{themeEmoji[world.theme] || '\u2694\uFE0F'} {world.name}</h1>
          <div className="map-header-stats">
            <span className="star-count">
              {'\u2605'.repeat(adventure.totalStars)} {adventure.totalStars} Stars
            </span>
            <span className="encounter-count">
              {adventure.encountersDefeated} Encounters Defeated
            </span>
          </div>
        </div>
        <div className="map-header-right">
          {adventure?.isSolo ? (
            <span className="partner-indicator solo-mode">Solo Adventure</span>
          ) : (
            <span className={`partner-indicator ${partnerConnected ? 'partner-online' : 'partner-offline'}`}>
              {partnerConnected ? 'Partner Online' : 'Partner Offline'}
            </span>
          )}
          <button className="save-exit-button" onClick={handleSaveAndExit}>
            Save &amp; Exit
          </button>
        </div>
      </div>

      {/* Notifications */}
      {notification && (
        <div className="map-notification">
          {notification}
        </div>
      )}

      {moveError && (
        <div className="map-notification map-notification-error">
          {moveError}
        </div>
      )}

      {/* Main Content */}
      <div className="map-content">
        {/* Hex Grid */}
        <div className="map-grid-section">
          <HexMapGrid
            hexes={world.mapDefinition.hexes}
            mapWidth={world.mapDefinition.width}
            mapHeight={world.mapDefinition.height}
            revealedHexes={revealedHexes}
            currentPosition={currentPosition}
            completedEncounters={adventure.completedEncounters}
            usedRestPoints={adventure.usedRestPoints}
            onHexClick={handleHexClick}
            onCurrentEncounterClick={handleCurrentEncounterClick}
          />
        </div>

        {/* Team Status Panel */}
        <div className="map-sidebar">
          <h3 className="sidebar-title">Team Status</h3>
          <TeamStatusPanel
            creatureStates={adventure.creatureStates}
            currentUserId={user?.uid || ''}
          />
        </div>
      </div>

      {/* Encounter Preview Modal */}
      {showEncounterModal && encounterPreview && (
        <EncounterPreviewModal
          encounter={encounterPreview.encounter as any}
          encounterId={encounterPreview.encounterId}
          onEnterBattle={handleEnterBattle}
          onClose={handleCloseEncounterModal}
        />
      )}
    </div>
    </PageAtmosphere>
  );
};
